import { useRef } from "react";

const never = Symbol("never");

/**
 * Utility type to enforce void result.
 *
 * This is needed because TypeScript `() => number` `() => void`
 */
// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
type StrictVoid = void | { [never]: never };

type AcceptableCallback =
  | ((...args: never[]) => StrictVoid)
  | ((...args: never[]) => Promise<unknown>);

/**
 * Sometimes a callback is just a callback and should not cause hooks/components
 * downstream to re-render.
 *
 * A litmus test often is whether a callback is called `on...` like `onClick`.
 *
 * Functions passed to `useEvent` must return `void` (or be async) to
 * prevent accidental misuse of `useEvent` where `useCallback` should
 * be used (like when using predicates or getters).
 *
 * Async functions are also allowed because it's very likely that they
 * are actions (e.g. `performRequest() => Promise<...>`).
 *
 * Resources:
 * - https://react.dev/reference/react/experimental_useEffectEvent
 * - https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event
 * - https://github.com/reactjs/rfcs/pull/220
 */
export function useEffectEvent<T extends Function & AcceptableCallback>(
  callback: T,
): T {
  const callbackRef = useRef<T>(callback);
  callbackRef.current = callback;

  const stableRef = useRef<Function>(null);
  stableRef.current ??= (...args: Parameters<T>) => {
    return callbackRef.current(...args);
  };

  return stableRef.current as T;
}

/**
 * Variant of `useEffectEvent` that allows non-void results.
 *
 * `useEffectEvent` doesn't permit non-void results to not accidentally
 * use a `useEffectEvent` where a `useCallback` should be used.
 *
 * If one is *really* sure, `useEffectEventWithResult` is available, e.g.
 * to report whether the dispatching of the event was successful.
 */
export function useEffectEventWithResult<T extends Function>(callback: T): T {
  // @ts-expect-error Our protection rightfully complains here
  return useEffectEvent(callback);
}
